<template>
  <div class="eye-exam-fields w-full flex flex-col gap-4" dir="ltr">
    <div class="grid gap-4 lg:grid-cols-2">
      <!-- Visual acuity -->
      <section class="ee-card">
        <div class="ee-card-header">
          <div class="ee-heading">
            <div class="ee-title">{{ en('visualAcuity') }}<bdi v-if="isArabic" class="ee-title-ar">{{ t('visualAcuity') }}</bdi></div>
            <div class="ee-subtitle"><bdi>{{ t('visualAcuitySubtitle') }}</bdi></div>
            <el-checkbox v-model="model.vaCatfordDrum" size="small" class="ee-catford"><bdi>{{ t('catfordDrum') }}</bdi></el-checkbox>
          </div>
        </div>
        <div class="ee-grid" style="--ee-cols: 2">
          <div></div>
          <div class="ee-col-head">VA WOG</div>
          <div class="ee-col-head">VA WG</div>
          <template v-for="eye in eyes" :key="eye">
            <EyeLabel :eye="eye" :label="en(eye === 'od' ? 'rightEye' : 'leftEye')"/>
            <el-select
                v-for="field in ['vaWithoutGlasses', 'vaWithGlasses']"
                :key="field"
                v-model="model[`${field}${eye.toUpperCase()}`]"
                filterable
                clearable
                default-first-option
                placeholder=" "
                class="w-full"
            >
              <el-option v-for="option in examinationOptions" :key="option" :label="option" :value="option"/>
            </el-select>
          </template>
        </div>
      </section>

      <!-- IOP -->
      <section class="ee-card">
        <div class="ee-card-header">
          <div class="ee-heading">
            <div class="ee-title">{{ en('iop') }}<bdi v-if="isArabic" class="ee-title-ar">{{ t('iop') }}</bdi></div>
            <div class="ee-subtitle"><bdi>{{ t('iopSubtitle') }}</bdi></div>
          </div>
        </div>
        <div class="ee-grid" style="--ee-cols: 2">
          <div></div>
          <div class="ee-col-head">IOP <span class="ee-unit">mmHg</span></div>
          <div class="ee-col-head">{{ en('iopStatus') }}</div>
          <template v-for="eye in eyes" :key="eye">
            <EyeLabel :eye="eye" :label="en(eye === 'od' ? 'rightEye' : 'leftEye')"/>
            <CustomNumberInput
                v-model="model[`iop${eye.toUpperCase()}`]"
                :min="0"
                :max="80"
                :step="1"
                :precision="0"
                :start-up="16"
                :start-down="16"
                clamp
            />
            <el-select
                v-model="model[`iop${eye.toUpperCase()}Status`]"
                clearable
                placeholder=" "
                class="w-full"
            >
              <el-option v-for="option in iopStatuses" :key="option" :label="option" :value="option"/>
            </el-select>
          </template>
        </div>
      </section>
    </div>

    <!-- Distance vision -->
    <section class="ee-card">
      <div class="ee-card-header">
        <div class="ee-heading">
          <div class="ee-title">{{ en('distance') }} <bdi class="ee-abbr">DV</bdi><bdi v-if="isArabic" class="ee-title-ar">{{ t('distance') }}</bdi></div>
          <div class="ee-subtitle"><bdi>{{ t('distanceSubtitle') }}</bdi></div>
        </div>
      </div>
      <div class="ee-grid ee-rx-grid">
        <div></div>
        <div class="ee-col-head">SPH</div>
        <div class="ee-col-head">CYL</div>
        <div class="ee-col-head">AXIS</div>
        <div class="ee-col-head ee-pd-head">PD <span class="ee-unit">mm</span></div>
        <template v-for="eye in eyes" :key="eye">
          <EyeLabel :eye="eye" :label="en(eye === 'od' ? 'rightEye' : 'leftEye')"/>
          <CustomNumberInput v-model="model[`distance${eye.toUpperCase()}SPH`]" v-bind="sphProps"/>
          <CustomNumberInput v-model="model[`distance${eye.toUpperCase()}CYL`]" v-bind="cylProps"/>
          <CustomNumberInput v-model="model[`distance${eye.toUpperCase()}AXIS`]" v-bind="axisProps"/>
          <div v-if="eye === 'od'" class="ee-pd-cell">
            <CustomNumberInput v-model="model.distanceODIPD" v-bind="pdProps"/>
          </div>
        </template>
      </div>
      <div v-if="distanceRx.some(rx => rx.text)" class="ee-rx-summary">
        <span v-for="rx in distanceRx" :key="rx.eye" class="ee-rx-chip">
          <b>{{ rx.eye.toUpperCase() }}</b> {{ rx.text || '—' }}
        </span>
      </div>
    </section>

    <!-- Near vision -->
    <section class="ee-card">
      <div class="ee-card-header">
        <div class="ee-heading">
          <div class="ee-title">{{ en('reading') }} <bdi class="ee-abbr">NV</bdi><bdi v-if="isArabic" class="ee-title-ar">{{ t('reading') }}</bdi></div>
          <div class="ee-subtitle"><bdi>{{ model.readingAdd ? t('readingAddSubtitle') : t('readingFullSubtitle') }}</bdi></div>
        </div>
      </div>
      <div class="ee-mode-row">
        <el-radio-group v-model="model.readingAdd" size="small" dir="ltr">
          <el-radio-button :label="true">{{ en('addMode') }}</el-radio-button>
          <el-radio-button :label="false">{{ en('fullRx') }}</el-radio-button>
        </el-radio-group>
        <el-button size="small" plain :disabled="!hasDistance" @click="copyFromDistance">
          <Icon icon="mdi:content-copy" class="me-1"/>
          <bdi>{{ t('copyFromDistance') }}</bdi>
        </el-button>
      </div>
      <div class="ee-grid ee-rx-grid">
        <div></div>
        <div class="ee-col-head">{{ model.readingAdd ? 'ADD' : 'SPH' }}</div>
        <div class="ee-col-head">CYL</div>
        <div class="ee-col-head">AXIS</div>
        <div class="ee-col-head ee-pd-head">PD <span class="ee-unit">mm</span></div>
        <template v-for="eye in eyes" :key="eye">
          <EyeLabel :eye="eye" :label="en(eye === 'od' ? 'rightEye' : 'leftEye')"/>
          <CustomNumberInput
              :model-value="model[`reading${eye.toUpperCase()}SPH`]"
              v-bind="model.readingAdd ? addProps : sphProps"
              @update:model-value="(value: number | undefined) => setReadingSPH(eye, value)"
          />
          <CustomNumberInput v-model="model[`reading${eye.toUpperCase()}CYL`]" v-bind="cylProps"/>
          <CustomNumberInput v-model="model[`reading${eye.toUpperCase()}AXIS`]" v-bind="axisProps"/>
          <div v-if="eye === 'od'" class="ee-pd-cell">
            <CustomNumberInput v-model="model.readingODIPD" v-bind="readingPdProps"/>
          </div>
        </template>
      </div>
      <div v-if="nearRx.some(rx => rx.text)" class="ee-rx-summary" :title="t('nearRxHint')">
        <span class="ee-rx-caption">{{ t('nearRx') }}</span>
        <span v-for="rx in nearRx" :key="rx.eye" class="ee-rx-chip">
          <b>{{ rx.eye.toUpperCase() }}</b> {{ rx.text || '—' }}
        </span>
      </div>
    </section>

    <!-- Contact lenses -->
    <section class="ee-card" :class="{ 'ee-card-collapsed': !contactOpen }">
      <button type="button" class="ee-card-header ee-toggle" :disabled="hasContact" @click="contactExpanded = !contactExpanded">
        <div class="ee-heading">
          <div class="ee-title">{{ en('contact') }} <bdi class="ee-abbr">CL</bdi><bdi v-if="isArabic" class="ee-title-ar">{{ t('contact') }}</bdi></div>
          <div class="ee-subtitle"><bdi>{{ t('contactSubtitle') }}</bdi></div>
        </div>
        <Icon v-if="!hasContact" :icon="contactOpen ? 'mdi:chevron-up' : 'mdi:chevron-down'" width="22"/>
      </button>
      <div v-if="contactOpen" class="ee-cl-groups">
        <div class="ee-cl-group ee-cl-eyes">
          <div class="ee-cl-group-title"></div>
          <div class="ee-col-head"></div>
          <EyeLabel v-for="eye in eyes" :key="eye" :eye="eye" :label="en(eye === 'od' ? 'rightEye' : 'leftEye')"/>
        </div>
        <div class="ee-cl-group" style="--ee-cols: 3">
          <div class="ee-cl-group-title">{{ en('clPower') }}<bdi v-if="isArabic" class="ee-cl-group-title-ar">{{ t('clPower') }}</bdi></div>
          <div class="ee-col-head">SPH</div>
          <div class="ee-col-head">CYL</div>
          <div class="ee-col-head">AXIS</div>
          <template v-for="eye in eyes" :key="eye">
            <CustomNumberInput v-model="model[`contact${eye.toUpperCase()}SPH`]" v-bind="sphProps"/>
            <CustomNumberInput v-model="model[`contact${eye.toUpperCase()}CYL`]" v-bind="cylProps"/>
            <CustomNumberInput v-model="model[`contact${eye.toUpperCase()}AXIS`]" v-bind="axisProps"/>
          </template>
        </div>
        <div class="ee-cl-group" style="--ee-cols: 3">
          <div class="ee-cl-group-title">{{ en('clKeratometry') }}<bdi v-if="isArabic" class="ee-cl-group-title-ar">{{ t('clKeratometry') }}</bdi></div>
          <div class="ee-col-head">K1</div>
          <div class="ee-col-head">K2</div>
          <div class="ee-col-head" :title="t('kAveHint')">K-AVE</div>
          <template v-for="eye in eyes" :key="eye">
            <CustomNumberInput
                v-for="k in ['K1', 'K2'] as const"
                :key="k"
                :model-value="model[`contact${eye.toUpperCase()}${k}`]"
                v-bind="kProps"
                @update:model-value="(value: number | undefined) => setKeratometry(eye, k, value)"
            />
            <CustomNumberInput v-model="model[`contact${eye.toUpperCase()}KAVE`]" v-bind="kProps"/>
          </template>
        </div>
        <div class="ee-cl-group" style="--ee-cols: 2">
          <div class="ee-cl-group-title">{{ en('clLensFit') }}<bdi v-if="isArabic" class="ee-cl-group-title-ar">{{ t('clLensFit') }}</bdi></div>
          <div class="ee-col-head">B.C.</div>
          <div class="ee-col-head">DIA</div>
          <template v-for="eye in eyes" :key="eye">
            <CustomNumberInput v-model="model[`contact${eye.toUpperCase()}BC`]" v-bind="bcProps"/>
            <CustomNumberInput v-model="model[`contact${eye.toUpperCase()}DIA`]" v-bind="diaProps"/>
          </template>
        </div>
      </div>
    </section>

    <!-- Prism -->
    <section class="ee-card" :class="{ 'ee-card-collapsed': !prismOpen }">
      <button type="button" class="ee-card-header ee-toggle" :disabled="hasPrism" @click="prismExpanded = !prismExpanded">
        <div class="ee-heading">
          <div class="ee-title">{{ en('prism') }}<bdi v-if="isArabic" class="ee-title-ar">{{ t('prism') }}</bdi></div>
          <div class="ee-subtitle"><bdi>{{ t('prismSubtitle') }}</bdi></div>
        </div>
        <Icon v-if="!hasPrism" :icon="prismOpen ? 'mdi:chevron-up' : 'mdi:chevron-down'" width="22"/>
      </button>
      <div v-if="prismOpen" class="ee-grid" style="--ee-cols: 3">
        <div></div>
        <div class="ee-col-head">PRISM <span class="ee-unit">Δ</span></div>
        <div class="ee-col-head">BASE</div>
        <div class="ee-col-head">AXIS</div>
        <template v-for="eye in eyes" :key="eye">
          <EyeLabel :eye="eye" :label="en(eye === 'od' ? 'rightEye' : 'leftEye')"/>
          <CustomNumberInput v-model="model[`prism${eye.toUpperCase()}`]" v-bind="prismProps"/>
          <el-select
              v-model="model[`prism${eye.toUpperCase()}BASE`]"
              filterable
              clearable
              placeholder=" "
              class="w-full"
          >
            <el-option v-for="option in prismOptions" :key="option" :label="option" :value="option"/>
          </el-select>
          <CustomNumberInput v-model="model[`prism${eye.toUpperCase()}AXIS`]" v-bind="axisProps"/>
        </template>
      </div>
    </section>
  </div>
</template>

<script setup lang="ts">
import {computed, defineComponent, h, ref, type PropType} from 'vue'
import {useI18n} from 'vue-i18n'
import {Icon} from '@iconify/vue'
import CustomNumberInput from '@/views/examinations/CustomNumberInput.vue'
import Examination, {
  eyeExamSectionHasValues,
  formatSphCylAxis,
  keratometryAverage,
  nearSPH,
  suggestedReadingPD
} from '@/models/customer/Examination'

const props = defineProps({
  examination: {
    type: Object as PropType<Examination>,
    required: true
  },
  examinationOptions: {
    type: Array as PropType<string[]>,
    default: () => []
  },
  prismOptions: {
    type: Array as PropType<string[]>,
    default: () => []
  }
})

const {t, locale} = useI18n({
  useScope: 'local',
  inheritLocale: true,
  messages: {
    en: {
      visualAcuity: 'Visual Acuity',
      visualAcuitySubtitle: 'Without glasses (WOG) and with glasses (WG)',
      catfordDrum: 'Measured with a Catford drum',
      iop: 'Intraocular Pressure',
      iopSubtitle: 'Measured value, or a status when it could not be measured',
      iopStatus: 'Status',
      distance: 'Distance',
      distanceSubtitle: 'Final distance prescription',
      reading: 'Near',
      readingAddSubtitle: 'ADD is added to the distance SPH; CYL and AXIS usually match the distance',
      readingFullSubtitle: 'Full near prescription',
      addMode: 'ADD',
      fullRx: 'Full Rx',
      copyFromDistance: 'Copy Values',
      nearRx: 'Near Rx',
      nearRxHint: 'Distance SPH + ADD',
      contact: 'Contact Lenses',
      contactSubtitle: 'Power, keratometry and lens fitting',
      kAveHint: 'Filled automatically from K1 and K2',
      clPower: 'Power',
      clKeratometry: 'Keratometry',
      clLensFit: 'Lens Fit',
      prism: 'Prism Glasses',
      prismSubtitle: 'Prism power, base direction and axis',
      rightEye: 'Right',
      leftEye: 'Left'
    },
    ar: {
      visualAcuity: 'حدة البصر',
      visualAcuitySubtitle: 'بدون نظارة (WOG) ومع النظارة (WG)',
      catfordDrum: 'تم القياس باستخدام Catford drum',
      iop: 'ضغط العين',
      iopSubtitle: 'القيمة المقاسة، أو الحالة عند تعذر القياس',
      iopStatus: 'الحالة',
      distance: 'نظارة البعيد',
      distanceSubtitle: 'الوصفة النهائية للبعيد',
      reading: 'نظارة القراءة',
      readingAddSubtitle: 'تُضاف قيمة ADD إلى SPH البعيد، وعادةً تكون CYL و AXIS مثل البعيد',
      readingFullSubtitle: 'الوصفة الكاملة للقريب',
      addMode: 'إضافة',
      fullRx: 'وصفة كاملة',
      copyFromDistance: 'نسخ القيم',
      nearRx: 'وصفة القريب',
      nearRxHint: 'SPH البعيد + ADD',
      contact: 'العدسات اللاصقة',
      contactSubtitle: 'القوة وقياس القرنية ومقاسات العدسة',
      kAveHint: 'تُحسب تلقائياً من K1 و K2',
      clPower: 'القوة',
      clKeratometry: 'قياس القرنية',
      clLensFit: 'مقاسات العدسة',
      prism: 'نظارة البريزم',
      prismSubtitle: 'قوة البريزم واتجاه القاعدة والمحور',
      rightEye: 'يمين',
      leftEye: 'يسار'
    }
  }
})

// The exam grid is always LTR; in Arabic, titles show English plus Arabic
const isArabic = computed(() => locale.value === 'ar')
const en = (key: string) => t(key, {}, {locale: 'en'})

const EyeLabel = defineComponent({
  props: {eye: {type: String, required: true}, label: {type: String, required: true}},
  setup(labelProps) {
    return () => h('div', {class: 'ee-eye'}, [
      h('span', {class: 'ee-eye-code'}, labelProps.eye.toUpperCase()),
      h('span', {class: 'ee-eye-name'}, labelProps.label)
    ])
  }
})

const eyes = ['od', 'os'] as const
type Eye = typeof eyes[number]
const iopStatuses = ['Error', 'Lost']

const sphProps = {step: 0.25, precision: 2, min: -30, max: 30, signed: true, clamp: true, startUp: 0.25, startDown: -0.25}
const addProps = {step: 0.25, precision: 2, min: 0, max: 4, signed: true, clamp: true, startUp: 1, startDown: 1}
const cylProps = {step: 0.25, precision: 2, min: -10, max: 10, signed: true, clamp: true, startUp: 0.25, startDown: -0.25}
const axisProps = {step: 5, precision: 0, min: 0, max: 180, clamp: true, startUp: 90, startDown: 180}
const pdProps = {step: 0.5, precision: 1, min: 40, max: 80, clamp: true, startUp: 64, startDown: 64}
const kProps = {step: 0.25, precision: 2, min: 35, max: 65, clamp: true, startUp: 43, startDown: 43}
const bcProps = {step: 0.1, precision: 2, min: 7, max: 10, clamp: true, startUp: 8.6, startDown: 8.6}
const diaProps = {step: 0.1, precision: 1, min: 11, max: 16.5, clamp: true, startUp: 14.2, startDown: 14.2}
const prismProps = {step: 0.5, precision: 2, min: 0.25, max: 60, clamp: true, startUp: 0.5, startDown: 0.5}
// Reading PD usually sits 2mm under the distance PD
const readingPdProps = computed(() => {
  const start = suggestedReadingPD(model.value.distanceODIPD) ?? 62
  return {...pdProps, startUp: start, startDown: start}
})

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const model = computed(() => props.examination as any)

const upper = (eye: Eye) => eye.toUpperCase() as 'OD' | 'OS'
const isEmpty = (value: unknown) => value == null || value === '' || (typeof value === 'number' && isNaN(value))

const hasDistance = computed(() => eyeExamSectionHasValues(props.examination, 'distance'))
const hasContact = computed(() => eyeExamSectionHasValues(props.examination, 'contact'))
const hasPrism = computed(() => eyeExamSectionHasValues(props.examination, 'prism'))
const contactExpanded = ref(false)
const prismExpanded = ref(false)
const contactOpen = computed(() => hasContact.value || contactExpanded.value)
const prismOpen = computed(() => hasPrism.value || prismExpanded.value)

const distanceRx = computed(() => eyes.map(eye => ({
  eye,
  text: formatSphCylAxis(model.value[`distance${upper(eye)}SPH`], model.value[`distance${upper(eye)}CYL`], model.value[`distance${upper(eye)}AXIS`])
})))

const nearRx = computed(() => eyes.map(eye => {
  const E = upper(eye)
  const reading = model.value[`reading${E}SPH`]
  const sph = model.value.readingAdd ? nearSPH(model.value[`distance${E}SPH`], reading) : reading
  return {eye, text: formatSphCylAxis(sph, model.value[`reading${E}CYL`], model.value[`reading${E}AXIS`])}
}))

function fillReadingFromDistance(eye: Eye, overwrite: boolean) {
  const E = upper(eye)
  for (const field of ['CYL', 'AXIS']) {
    const source = model.value[`distance${E}${field}`]
    if ((overwrite || isEmpty(model.value[`reading${E}${field}`])) && !isEmpty(source)) {
      model.value[`reading${E}${field}`] = source
    }
  }
  if (eye === 'od' && (overwrite || isEmpty(model.value.readingODIPD))) {
    const pd = suggestedReadingPD(model.value.distanceODIPD)
    if (pd != null) model.value.readingODIPD = pd
  }
}

// Typing the OD near SPH/ADD mirrors it to OS while OS was empty or still matched OD.
// In Full Rx mode it also fills empty near CYL/AXIS/PD from the distance values;
// in ADD mode that only happens through the Copy Values button (shown in both modes).
function setReadingSPH(eye: Eye, value: number | undefined) {
  const E = upper(eye)
  const previous = model.value[`reading${E}SPH`]
  model.value[`reading${E}SPH`] = value
  if (isEmpty(value)) return
  const copyDistance = !model.value.readingAdd
  if (copyDistance) fillReadingFromDistance(eye, false)
  if (eye === 'od') {
    const os = model.value.readingOSSPH
    if (isEmpty(os) || os === previous) {
      model.value.readingOSSPH = value
      if (copyDistance) fillReadingFromDistance('os', false)
    }
  }
}

function copyFromDistance() {
  eyes.forEach(eye => fillReadingFromDistance(eye, true))
}

// K-AVE follows K1/K2 unless it was edited by hand
function setKeratometry(eye: Eye, k: 'K1' | 'K2', value: number | undefined) {
  const E = upper(eye)
  const previousAverage = keratometryAverage(model.value[`contact${E}K1`], model.value[`contact${E}K2`])
  model.value[`contact${E}${k}`] = value
  const current = model.value[`contact${E}KAVE`]
  if (isEmpty(current) || current === previousAverage) {
    const average = keratometryAverage(model.value[`contact${E}K1`], model.value[`contact${E}K2`])
    model.value[`contact${E}KAVE`] = average ?? undefined
  }
}
</script>

<style scoped>
.ee-catford {
  height: auto;
  margin-top: 0.25rem;
}

.ee-catford :deep(.el-checkbox__label) {
  @apply text-sm font-bold text-gray-700;
}

.ee-card {
  @apply rounded-lg border border-gray-200 bg-white px-4 py-3;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
}

.ee-card-header {
  @apply flex flex-wrap items-center justify-between gap-3 mb-3 w-full;
  text-align: start;
}

.ee-mode-row {
  @apply flex items-center gap-2 flex-wrap mb-3;
}

/* The Arabic theme (rtlcss) mirrors radio-button borders; keep this English toggle drawn LTR */
.ee-mode-row :deep(.el-radio-button__inner) {
  border-left: 0;
  border-right: var(--el-border);
  border-radius: 0;
}

.ee-mode-row :deep(.el-radio-button:first-child .el-radio-button__inner) {
  border-left: var(--el-border);
  border-radius: var(--el-border-radius-base) 0 0 var(--el-border-radius-base);
}

.ee-mode-row :deep(.el-radio-button:last-child .el-radio-button__inner) {
  border-radius: 0 var(--el-border-radius-base) var(--el-border-radius-base) 0;
}

.ee-mode-row :deep(.el-radio-button.is-active .el-radio-button__inner) {
  border-color: var(--el-radio-button-checked-border-color, var(--el-color-primary));
  box-shadow: -1px 0 0 0 var(--el-radio-button-checked-border-color, var(--el-color-primary));
}

.ee-mode-row :deep(.el-radio-button:first-child.is-active .el-radio-button__inner) {
  box-shadow: none;
}

.ee-card-collapsed .ee-card-header {
  @apply mb-0;
}

.ee-toggle {
  cursor: pointer;
}

.ee-toggle:disabled {
  cursor: default;
}

.ee-heading {
  @apply flex-1;
  border-inline-start: 3px solid var(--el-color-primary);
  padding-inline-start: 0.6rem;
}

.ee-title {
  @apply font-bold text-base;
}

.ee-abbr {
  @apply text-xs font-bold text-gray-500 ms-1;
}

.ee-title-ar {
  @apply font-bold;
  margin-left: 0.75rem;
}

.ee-subtitle {
  @apply text-xs text-gray-500;
}

.ee-grid {
  --ee-cols: 4;
  display: grid;
  grid-template-columns: 64px repeat(var(--ee-cols), minmax(0, 140px));
  gap: 0.5rem 0.75rem;
  align-items: center;
}

.ee-rx-grid {
  --ee-cols: 4;
}

.ee-cl-groups {
  --ee-cl-row: 34px;
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
}

/* Power / Keratometry / Lens Fit, split by a light line; rows have fixed heights so the boxes line up with the eye labels */
.ee-cl-group {
  --ee-cols: 3;
  flex: 0 1 auto;
  min-width: 0;
  display: grid;
  grid-template-columns: repeat(var(--ee-cols), minmax(0, 112px));
  grid-template-rows: 18px auto var(--ee-cl-row) var(--ee-cl-row);
  gap: 0.5rem 0.75rem;
  align-items: center;
  padding: 8px 10px;
  border: 1px solid transparent;
}

.ee-cl-group ~ .ee-cl-group ~ .ee-cl-group {
  border-inline-start-color: #e5e7eb;
}

.ee-cl-group .ee-col-head {
  min-height: 20px;
}

.ee-cl-eyes {
  --ee-cols: 1;
  flex: 0 0 auto;
  grid-template-columns: 54px;
  padding-inline: 0;
  border-color: transparent;
  background: none;
}

.ee-cl-group-title {
  grid-column: 1 / -1;
  display: flex;
  gap: 0.5rem;
  overflow: hidden;
  white-space: nowrap;
  @apply text-xs font-bold;
  line-height: 18px;
  color: #1e4f7b;
}

.ee-col-head {
  @apply font-bold text-sm text-center text-gray-700;
}

.ee-unit {
  @apply font-normal text-xs text-gray-400;
}

.ee-pd-cell {
  grid-row: span 2;
  align-self: center;
}

.ee-rx-summary {
  @apply flex flex-wrap items-center gap-2 mt-3;
  padding-inline-start: 64px;
}

.ee-rx-caption {
  @apply text-xs font-bold text-gray-500;
}

.ee-rx-chip {
  @apply text-sm rounded px-2 py-0.5;
  color: #1e4f7b;
  background: #dbecfc;
  border: 1px solid #b6d4f2;
}

:deep(.ee-eye) {
  @apply flex flex-col items-end leading-tight pe-1;
}

:deep(.ee-eye-code) {
  @apply font-bold;
}

:deep(.ee-eye-name) {
  @apply text-[11px] text-gray-500;
}
/* Phones only: desktop layout above is unchanged */
@media (max-width: 767px) {
  .ee-card {
    @apply px-3;
  }

  .ee-card-header :deep(.el-button) {
    white-space: normal;
    height: auto;
    min-height: 24px;
  }

  .ee-grid {
    grid-template-columns: 40px repeat(var(--ee-cols), minmax(0, 1fr));
    gap: 0.5rem;
  }

  /* SPH / CYL / AXIS per eye, PD on its own row underneath */
  .ee-rx-grid {
    grid-template-columns: 40px repeat(3, minmax(0, 1fr));
  }

  .ee-rx-grid .ee-pd-head {
    grid-row: 4;
    grid-column: 1;
    text-align: end;
  }

  .ee-rx-grid .ee-pd-cell {
    grid-row: 4;
    grid-column: 2;
  }

  /* The three contact-lens boxes scroll sideways with the eye labels pinned */
  .ee-cl-groups {
    overflow-x: auto;
    padding-bottom: 6px;
    gap: 0.5rem;
  }

  .ee-cl-group {
    flex-shrink: 0;
    grid-template-columns: repeat(var(--ee-cols), 80px);
    gap: 0.5rem;
    padding: 8px;
  }

  .ee-cl-eyes {
    grid-template-columns: 40px;
    padding-inline: 0;
    position: sticky;
    left: 0;
    z-index: 1;
    align-self: stretch;
    background: #fff;
  }

  .ee-rx-summary {
    padding-inline-start: 0;
  }

  /* Keep the Arabic title in one piece so it wraps as a block with its gap */
  .ee-title-ar {
    display: inline-block;
  }

  .ee-grid :deep(input) {
    padding-left: 2px;
    padding-right: 2px;
    font-size: 14px;
  }
}
</style>
